// frontend/packages/business-console/src/contexts/AuthContext.js

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiScopes, DEFAULT_API_SCOPES } from '../auth/msalConfig';
import { authApi, setApiTokenRefresher } from '@upryzze/services';
// 5.9 — wipe the IndexedDB customer cache on logout. D8: bounded
// PII on local storage; must not survive a session change on a
// shared device.
import { clearAllCustomerCache } from '../lib/customerCache';

const TOKEN_STORAGE_KEY = 'token';
const USER_STORAGE_KEY = 'user';
const ACTIVE_BUSINESS_ID_KEY = 'activeBusinessId';

// Refresh token a bit before expiry (avoid edge-of-expiry 401s)
const TOKEN_GRACE_MS = 2 * 60 * 1000; // 2 minutes

// How often to proactively check the token during a live session. ensureSignedIn
// is a no-op until the token is within TOKEN_GRACE_MS of expiry, so this poll is
// cheap; it keeps long sessions (e.g. the /build interview) from hitting a 401.
const TOKEN_REFRESH_POLL_MS = 60 * 1000; // 1 minute

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

const readSession = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSession = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // ignore
  }
};

const removeSession = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const clearAuthStorage = () => {
  try {
    removeSession(TOKEN_STORAGE_KEY);
    removeSession(USER_STORAGE_KEY);
    removeSession(ACTIVE_BUSINESS_ID_KEY);

    // legacy keys (safe to remove)
    removeSession('businessAccess');
    removeSession('featureFlags');
  } catch {
    // ignore
  }
};

const parseJwtExpMs = (token) => {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return 0;
    const payload = JSON.parse(atob(parts[1]));
    const exp = Number(payload?.exp || 0);
    return exp ? exp * 1000 : 0;
  } catch {
    return 0;
  }
};

const resolveBusinessId = (u) => {
  if (!u) return null;

  if (u.activeBusinessId) return u.activeBusinessId;

  // Prefer the user's OWN business — INCLUDING staff accounts that also
  // belong to a business. A staff member who owns/admins a business
  // shouldn't be forced into the staff landing for their own console.
  // (Previously any `isStaff` user returned null here, which is why a
  // staff-owner always defaulted into "staff mode" for their own data.)
  const access = Array.isArray(u.businessAccess) ? u.businessAccess : [];
  if (access.length === 1) return access[0].businessId;

  // 0 entries => pure staff (reach tenants via the ClientSwitcher) or an
  // unassociated user. >1 => policy violation handled at login. Either
  // way, don't auto-pick.
  return null;
};

// True when `bizId` is one of the user's own memberships (businessAccess).
// Distinguishes "my own business" from "a client I'm scoped into as staff".
const ownsBusiness = (u, bizId) => {
  if (!u || !bizId) return false;
  const access = Array.isArray(u.businessAccess) ? u.businessAccess : [];
  return access.some((a) => a && String(a.businessId) === String(bizId));
};

const AuthContext = createContext({
  user: null,
  businessId: null,
  loading: true,
  isStaff: false,
  isImpersonating: false,

  ensureSignedIn: async () => false,
  refreshUser: async () => {},
  logout: async () => {},

  // Switch the active business (sets sessionStorage + updates user).
  // For staff this drives the ClientSwitcher; for tenant users it's
  // effectively a no-op since they have exactly one business.
  setActiveBusinessId: () => null,

  // optional helpers
  getToken: () => '',
});

export const AuthProvider = ({ children }) => {
  const { instance: msalInstance } = useMsal();

  const [user, setUser] = useState(() =>
    safeJsonParse(readSession(USER_STORAGE_KEY), null)
  );
  const [loading, setLoading] = useState(true);

  const bootRef = useRef(false);
  const acquiringRef = useRef(false);
  const scopesRef = useRef(DEFAULT_API_SCOPES);

  // Load scopes once (best-effort)
  useEffect(() => {
    let alive = true;
    getApiScopes()
      .then((scopes) => {
        if (!alive) return;
        if (Array.isArray(scopes) && scopes.length) scopesRef.current = scopes;
      })
      .catch(() => {
        if (!alive) return;
        scopesRef.current = DEFAULT_API_SCOPES;
      });

    return () => {
      alive = false;
    };
  }, []);

  const getToken = useCallback(() => {
    return readSession(TOKEN_STORAGE_KEY) || '';
  }, []);

  const setActiveBusinessId = useCallback((businessId) => {
    const normalized = businessId ? String(businessId) : '';
    if (normalized) writeSession(ACTIVE_BUSINESS_ID_KEY, normalized);
    else removeSession(ACTIVE_BUSINESS_ID_KEY);

    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, activeBusinessId: normalized || null };
      writeSession(USER_STORAGE_KEY, JSON.stringify(next));
      return next;
    });

    return normalized || null;
  }, []);

  const ensureSignedIn = useCallback(async () => {
    if (!msalInstance) return false;

    const account =
      msalInstance.getActiveAccount() ||
      (msalInstance.getAllAccounts()?.[0] ?? null);
    if (!account) return false;

    const storedToken = getToken();
    if (storedToken) {
      const expMs = parseJwtExpMs(storedToken);
      if (expMs && expMs - Date.now() > TOKEN_GRACE_MS) return true;
    }

    // prevent thundering-herd token acquisition
    if (acquiringRef.current) return Boolean(getToken());
    acquiringRef.current = true;

    try {
      const scopes = scopesRef.current?.length
        ? scopesRef.current
        : DEFAULT_API_SCOPES;
      const resp = await msalInstance.acquireTokenSilent({ account, scopes });
      if (resp?.accessToken) {
        writeSession(TOKEN_STORAGE_KEY, resp.accessToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      acquiringRef.current = false;
    }
  }, [getToken, msalInstance]);

  const hydrateUserFromApi = useCallback(async () => {
    const { data: me } = await authApi.get('/api/auth/me');

    // Prefer MSAL token claims for display name — avoids stale database
    // defaults (e.g. "User Account") that haven't been updated via Profile.
    const account =
      msalInstance?.getActiveAccount?.() ||
      msalInstance?.getAllAccounts?.()?.[0];
    const claims = account?.idTokenClaims || {};

    const storedActiveBusinessId = readSession(ACTIVE_BUSINESS_ID_KEY);
    const resolvedBusinessId = storedActiveBusinessId || resolveBusinessId(me);

    const nextUser = {
      ...(me || {}),
      firstName: claims.given_name || me?.firstName || '',
      lastName: claims.family_name || me?.lastName || '',
      activeBusinessId: resolvedBusinessId || null,
    };

    writeSession(USER_STORAGE_KEY, JSON.stringify(nextUser));

    if (resolvedBusinessId)
      writeSession(ACTIVE_BUSINESS_ID_KEY, String(resolvedBusinessId));
    else removeSession(ACTIVE_BUSINESS_ID_KEY);

    setUser(nextUser);
  }, [msalInstance]);

  const refreshUser = useCallback(async () => {
    const ok = await ensureSignedIn();
    if (!ok) return;
    await hydrateUserFromApi();
  }, [ensureSignedIn, hydrateUserFromApi]);

  const logout = useCallback(async () => {
    // Clear local state immediately (snappy UI)
    clearAuthStorage();
    setUser(null);

    // 5.9 — wipe cached customer snapshots (D8). Best-effort; we
    // don't block logout on this. The IndexedDB writes are
    // origin-scoped so the failure mode (cache survives one
    // session) is bounded to this browser anyway.
    try {
      await clearAllCustomerCache();
    } catch {
      // ignore
    }

    // Best-effort server logout (optional; depends on backend)
    try {
      await authApi.post('/api/auth/logout');
    } catch {
      // ignore
    }

    // MSAL sign-out (redirect)
    try {
      if (!msalInstance) return;
      const account =
        msalInstance.getActiveAccount() ||
        (msalInstance.getAllAccounts()?.[0] ?? undefined);
      await msalInstance.logoutRedirect({ account });
    } catch {
      // fallback: if redirect fails, just send user to login
      window.location.assign('/login');
    }
  }, [msalInstance]);

  // Boot: hydrate if not on /login
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;

    const path =
      (typeof window !== 'undefined' && window.location?.pathname) || '';
    const isAuthRoute =
      path.startsWith('/login') || path.startsWith('/auth/callback');

    (async () => {
      try {
        if (!isAuthRoute) {
          await refreshUser();
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [refreshUser]);

  // Route the api client's 401 refresh through MSAL silent acquisition. The
  // backend has no /api/auth/refresh endpoint, so without this every expired
  // token bounced the user to /login mid-session. ensureSignedIn re-acquires
  // silently; a null return means the session is truly gone (then we log out).
  useEffect(() => {
    setApiTokenRefresher(async () => {
      const ok = await ensureSignedIn();
      return ok ? getToken() : null;
    });
    return () => setApiTokenRefresher(null);
  }, [ensureSignedIn, getToken]);

  // Proactively keep the token fresh during long sessions (e.g. /build).
  // ensureSignedIn no-ops until within TOKEN_GRACE_MS of expiry, so the poll
  // is cheap; this prevents the 401 in the first place.
  useEffect(() => {
    if (!msalInstance) return undefined;
    const id = setInterval(() => {
      ensureSignedIn().catch(() => {});
    }, TOKEN_REFRESH_POLL_MS);
    return () => clearInterval(id);
  }, [ensureSignedIn, getToken, msalInstance]);

  const businessId = useMemo(() => {
    const stored = readSession(ACTIVE_BUSINESS_ID_KEY);
    return stored || resolveBusinessId(user);
  }, [user]);

  const value = useMemo(
    () => ({
      user,
      businessId: businessId || null,
      loading,
      isStaff: user?.isStaff === true,
      // Only true when a staff member is scoped into a business they do
      // NOT belong to (real impersonation). False for a staff member
      // viewing their own business — that's normal mode, no banner.
      isImpersonating:
        user?.isStaff === true &&
        Boolean(businessId) &&
        !ownsBusiness(user, businessId),

      ensureSignedIn,
      refreshUser,
      logout,

      getToken,

      setActiveBusinessId,
    }),
    [
      businessId,
      ensureSignedIn,
      getToken,
      loading,
      logout,
      refreshUser,
      setActiveBusinessId,
      user,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
